import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "@components/Button";
import { Checkbox, CheckboxSize } from "@components/Checkbox";
import { TextArea } from "@components/TextInput";
import React, { useEffect, useRef } from "react";

import { useNpsContext } from "src/context/NpsContext";
import {
  nextNpsStep,
  submitNpsCheckboxes,
} from "src/context/NpsContext/actions";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";
import { FontWeight } from "src/theme/text";

const OTHER_OPTION = "Other (please specify below)";

const getQuestionForScore = (score: number) => {
  if (score >= 0 && score <= 6) {
    return "What could we improve?";
  } else if (score >= 7 && score <= 8) {
    return "What should we know?";
  } else if (score >= 9 && score <= 10) {
    return "What do you love most?";
  }
  return "";
};

const getOptionsForScore = (score: number) => {
  if (score >= 0 && score <= 6) {
    return [
      "Platform is hard to use",
      "Missing features I need",
      "Had technical problems",
      "Donation processing issues",
      "Dashboard is confusing",
      OTHER_OPTION,
    ];
  } else if (score >= 7 && score <= 8) {
    return [
      "Easy to use",
      "Accepts crypto/stock/DAF donations",
      "Great customer support",
      "Missing features I need",
      "Platform is hard to use",
      OTHER_OPTION,
    ];
  } else if (score >= 9 && score <= 10) {
    return [
      "Easy to use",
      "Accepts crypto/stock/DAF donations",
      "Great customer support",
      "Simple setup",
      "Good donor experience",
      OTHER_OPTION,
    ];
  }
  return [];
};

export const NpsCheckboxes: React.FCC = () => {
  const { selectedScore, userKind, nonprofitId } = useNpsContext();
  const [selectedOptions, setSelectedOptions] = React.useState<string[]>([]);
  const hasOtherOptionSelected = selectedOptions.includes(OTHER_OPTION);
  const [otherText, setOtherText] = React.useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const onSubmit = () => {
    if (userKind && selectedScore !== null) {
      submitNpsCheckboxes({
        question: getQuestionForScore(selectedScore),
        selections: [
          ...selectedOptions,
          otherText ? `Other: ${otherText}` : "",
        ].filter(Boolean),
        userKind,
        nonprofitId,
      });
    }
    nextNpsStep();
  };

  useEffect(() => {
    if (hasOtherOptionSelected) {
      textareaRef.current?.focus();
    }
  }, [hasOtherOptionSelected]);

  if (selectedScore === null) {
    return null;
  }

  const options = getOptionsForScore(selectedScore);
  const question = getQuestionForScore(selectedScore);

  return (
    <div css={verticalStackCss.m}>
      <p css={{ fontWeight: FontWeight.BOLD }}>{question}</p>
      <div css={verticalStackCss.xs}>
        {options.map((option) => (
          <Checkbox
            key={option}
            size={CheckboxSize.LARGE}
            css={[horizontalStackCss.xs]}
            data-tname="coverCostsCheckbox"
            checked={selectedOptions.includes(option)}
            onChange={() => {
              setSelectedOptions((prev) =>
                prev.includes(option)
                  ? prev.filter((o) => o !== option)
                  : [...prev, option]
              );
            }}
          >
            {option}
          </Checkbox>
        ))}
        {hasOtherOptionSelected && (
          <TextArea
            ref={textareaRef}
            data-tname="nps_other_textarea"
            collapseDescriptionSpace
            value={otherText}
            onChange={(e) => setOtherText(e.currentTarget.value)}
          />
        )}
      </div>
      <div css={[horizontalStackCss.s, { justifyContent: "flex-end" }]}>
        <Button
          data-tname="NpsCheckboxes--Submit"
          role={ButtonRole.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: onSubmit,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};
